---
title: "Workbench: hands-on challenge interviews"
description: "Run hands-on challenge interviews in Chosen's Workbench: repo-grounded tasks, async take-homes with a candidate link, auto-scored sessions, and replays."
category: "workbench"
keywords:
  - "take home coding challenge"
  - "async technical interview"
  - "coding challenge ATS"
  - "repo based interview"
  - "interview session replay"
schema: "HowTo"
nav:
  section: "guides"
  order: 36
---

The Workbench gives candidates a real working environment — an editor, a terminal, runnable checks — instead of a whiteboard and a guess. You attach a **challenge** to an interview stage; every candidate in that stage gets the same one, which is what makes comparing them honest. Candidates can see every check the grader runs — there are no hidden test cases.

Challenges run two ways: as an **async take-home** the candidate does on their own time, or as a **live session** worked during the call. Either way, [Interviews](/guides/interviews) is where the results land.

## Enabling a challenge on a stage

<Steps>
  <li>Open **Interviews → Challenges** with a position selected in the sidebar. Each interview stage gets a row.</li>
  <li>Click **Configure** on a stage and flip **Challenge on this stage** on.</li>
  <li>Pick a kind (coding, architecture, code review, debugging) and either a **preset** scenario or a repo of your own — see below.</li>
  <li>Tell it what you want to see. A sentence is enough — "how they handle a messy legacy service", say.</li>
  <li>Hit **Generate now**. Choosie drafts the brief, tasks, checks and rubric, then bakes the workspace image. The chip walks through **Generating** and **Building** to **Ready** — building takes a few minutes.</li>
</Steps>

If generation fails, the chip says so and the sheet shows why, with a **Retry**. The preview tabs (Brief, Tasks & checks, Diagrams, Rubric) show exactly what the candidate will get — read the brief before you send anyone in.

## Grounding a challenge in your repo

Preset scenarios are fine. Your actual codebase is better — the candidate works on code that looks like yours, and the challenge stops feeling like homework from a textbook.

Connect GitHub from **Settings → Integrations** (or the **Connect GitHub** button in the challenge sheet), install the app on the repos you're willing to use, then pick a repo in the sheet — paste `owner/repo`, a GitHub URL, or choose from the connected list. An optional branch/ref pins what gets snapshotted. Once a repo is set, the preset choice disappears; the repo is the scenario now.

If the button says it's not available on your environment, GitHub isn't configured for your deployment yet — presets still work.

## Sending an async take-home

Take-homes ride the normal [scheduling wizard](/guides/scheduling). When the stage you pick has a ready challenge, a **Format** choice appears: live session or async take-home.

<Steps>
  <li>Choose **Async take-home**. The interviewer and meeting-link steps disappear — there's no meeting.</li>
  <li>Set the **complete-by** date (default: a week out) and the time budget. The clock starts when the candidate opens the workspace, not when you send the email.</li>
  <li>Create the invites. You get a **workspace link per candidate** — copy it into your own email. The link is the invitation; Chosen doesn't email the candidate for you here.</li>
</Steps>

If a link shows as still provisioning, give it a minute and copy it from the row on the Interviews page instead.

## Reading the results

When a candidate submits, the scorecard shows a **work session** block on top: active time, how the code arrived (typed vs. pasted vs. AI-generated), prompt counts, check runs, tab-aways, and chapter markers for how the session unfolded. Async sessions arrive **auto-scored** — graded from the checks and the session evidence, marked with an amber badge. Your review supersedes it: score each criterion yourself and submit like any other scorecard. The auto-score is a first read, not a verdict.

**Watch replay** opens the full session — every keystroke, command, and AI exchange, replayable. Candidates know all of this is captured; it's shown to them before they start.

## Live follow-ups

To see a strong take-home candidate think in real time, use **Set up live follow-up** on the session. It creates a follow-up interview seeded from their submitted work — schedule it like any other meeting and the candidate picks up where they left off, with you watching. Available once the session is submitted.
